import { CSSProperties, useEffect, useRef, useState } from "react";
import { TGenericGUIDName, TProcessStates } from "../../../lib/types/general";
import { ERROR_IS_FRAUD } from "../../../lib/common/constants";
import { LabSignUpFormData, TApiAddress, TApiLab } from "../../../lib/types/labs";
import { ErrorMessageFraud } from "../../common/alert-message";
import { Spinner as SpinnerSmall } from "../../common/spinner";
import { FileUploaderComponent, useFileManagement } from "../../../services/file-management";
import { NameSection } from "./name-section";
import { MessageBtn } from "../../common/message-button";
import { AboutTheLabSection } from "./about-the-lab-section";
import { getCacheKillQueryString } from "../../../lib/common/api";
import { PositionsSection } from "./position-section";
import { useAuthContext } from "../../../contexts/auth-context";
import { InviteButton } from "../../common/invite-button";
import { editableStyle } from "./common";
import { getRegions } from "../../../services/regions";
import { getIndustryRoles } from "../../../services/generalInfo";
import { GallerySection } from "./gallery-section";
import { TEmployeeRole } from "../../../lib/types/employee_roles";
import { LogoLab } from "../../logo-lab";

type TAboutOurAreaProps = {
    editable: boolean;
    isSomePartBeingEdited: boolean;
    userInfo: TApiLab;
    workingState: TProcessStates;
    setUserInfo: React.Dispatch<React.SetStateAction<TApiLab>>;
    setIsSomePartBeingEdited: (value: React.SetStateAction<boolean>) => void;
    updateLabAndLocalStorage: (data: LabSignUpFormData, name?: string) => Promise<void>;
    setInappropriateContent: (value: boolean) => void;
    setUnsupportedImageType: (value: boolean) => void;
    setUploadErrorMessage: (value: string) => void;
    userId: string | undefined;
    userLatitude: number;
    userLongitude: number;
    coordinatesHandler: (userId: number) => Promise<void>;
    verifyDuplicateNameAddress: (name: string, address: TApiAddress, userId: number) => Promise<boolean>;
    onboardingStepCompleted?: () => void;
};

export const PartFirst = ({
    editable,
    isSomePartBeingEdited,
    userInfo,
    workingState,
    setUserInfo,
    setIsSomePartBeingEdited,
    updateLabAndLocalStorage,
    setInappropriateContent,
    setUnsupportedImageType,
    setUploadErrorMessage,
    userId,
    userLatitude,
    userLongitude,
    coordinatesHandler,
    verifyDuplicateNameAddress,
    onboardingStepCompleted,
}: TAboutOurAreaProps) => {
    if (editable && !onboardingStepCompleted) {
        throw new Error("PartFirst requires 'onboardingStepCompleted' when 'editable' is true");
    }
    const { userInfo: currentUserInfo } = useAuthContext();
    const { getImagePath, makeDroppableCapable } = useFileManagement();

    const [internalPhoto, setInternalPhoto] = useState<string | undefined>(
        getImagePath("internal", "fixed", !editable ? userInfo.guid : undefined) + "?" + getCacheKillQueryString(),
    );
    const [externalPhoto, setExternalPhoto] = useState<string | undefined>(
        getImagePath("external", "fixed", !editable ? userInfo.guid : undefined) + "?" + getCacheKillQueryString(),
    );
    const [isLogoCustomizing, setIsLogoCustomizing] = useState(false);
    const refProfileInternalPhoto = useRef<HTMLDivElement>(null);
    const refProfileExternalPhoto = useRef<HTMLDivElement>(null);

    const updateInternalPhoto = (newfile?: string, inappropriateContent?: boolean, unsupportedImageType?: boolean, errorMessage?: string) => {
        if (errorMessage) {
            window.scrollTo({ top: 0 });
            setUploadErrorMessage(errorMessage);
            return;
        }
        if (inappropriateContent) {
            window.scrollTo({ top: 0 });
            setUploadErrorMessage("");
            setInappropriateContent(true);
            setInternalPhoto(undefined);
        } else if (unsupportedImageType) {
            window.scrollTo({ top: 0 });
            setUploadErrorMessage("");
            setUnsupportedImageType(true);
            setInternalPhoto(undefined);
        } else {
            if (!newfile) {
                setInternalPhoto(undefined);
            } else {
                const cacheKill = editable ? `?${getCacheKillQueryString()}` : "";
                setUploadErrorMessage("");
                setInappropriateContent(false);
                setUnsupportedImageType(false);
                setInternalPhoto(getImagePath(newfile, "fixed", !editable ? userInfo.guid : undefined) + cacheKill);
            }
        }
    };

    const updateExternalPhoto = (newfile?: string, inappropriateContent?: boolean, unsupportedImageType?: boolean, errorMessage?: string) => {
        if (errorMessage) {
            window.scrollTo({ top: 0 });
            setUploadErrorMessage(errorMessage);
            return;
        }
        if (inappropriateContent) {
            window.scrollTo({ top: 0 });
            setUploadErrorMessage("");
            setInappropriateContent(true);
            setExternalPhoto(undefined);
        } else if (unsupportedImageType) {
            window.scrollTo({ top: 0 });
            setUploadErrorMessage("");
            setUnsupportedImageType(true);
            setExternalPhoto(undefined);
        } else {
            if (!newfile) {
                setExternalPhoto(undefined);
            } else {
                const cacheKill = editable ? `?${getCacheKillQueryString()}` : "";
                setUploadErrorMessage("");
                setInappropriateContent(false);
                setUnsupportedImageType(false);
                setExternalPhoto(getImagePath(newfile, "fixed", !editable ? userInfo.guid : undefined) + cacheKill);
            }
        }
    };

    const [isEditingPhilosophy, setIsEditingPhilosophy] = useState(false);
    const philosophyRef = useRef<any>();

    const PhilosophySection = () => {
        const [showError, setShowError] = useState(false);
        const [currWorkingState, setCurrWorkingState] = useState<TProcessStates>("done");
        const editableOnClickHandler = (e: any) => {
            if (!editable) return;
            if (isSomePartBeingEdited) return;
            e.preventDefault();
            e.stopPropagation();
            setIsEditingPhilosophy(true);
            setIsSomePartBeingEdited(true);
        };

        const savePhilosophy = async () => {
            const newData = {
                ...userInfo.other,
                philosophy: philosophyRef.current.value as string,
            };
            setCurrWorkingState("working");
            try {
                setShowError(false);
                await updateLabAndLocalStorage(
                    {
                        password: "",
                        ...newData,
                    },
                    "",
                );
                setUserInfo({
                    ...userInfo,
                    other: newData,
                });
                setIsEditingPhilosophy(false);
            } catch (error: any) {
                if (ERROR_IS_FRAUD(error)) {
                    setShowError(true);
                }

                setIsEditingPhilosophy(true);
            }
            setIsSomePartBeingEdited(false);
            setCurrWorkingState("done");
        };

        const cancelHandler = async () => {
            if (philosophyRef.current) {
                philosophyRef.current.value = userInfo?.other?.philosophy || "";
            }
            setShowError(false);
            setIsEditingPhilosophy(false);
            setIsSomePartBeingEdited(false);
        };

        return (
            <>
                <span id="fill_philosophy-progress"></span>
                {!isEditingPhilosophy && (
                    <>
                        <div className="p-0">
                            {editable && !isSomePartBeingEdited && (
                                <div className="fileTooltip rightEdit ms-auto" role="button" data-tooltip=" Edit " onClick={editableOnClickHandler}>
                                    <i className="fa-solid fa-pen-to-square fa-xs p-2 shadow editIcon" data-testid="edit-icon"></i>
                                </div>
                            )}
                        </div>
                        <h4
                            className={"fw-bold " + (workingState !== "done" || currWorkingState !== "done" ? "cssLoading" : "")}
                            style={editable && !isSomePartBeingEdited ? editableStyle : {}}
                            onClick={(e) => {
                                if (isSomePartBeingEdited) return;
                                editableOnClickHandler(e);
                            }}
                        >
                            Our Philosophy
                        </h4>
                        <p className={"lh-base px-2 text-break " + (workingState !== "done" || currWorkingState !== "done" ? "cssLoading" : "")}>
                            {philosophyRef.current?.value || userInfo.other.philosophy || "   "}
                        </p>
                    </>
                )}
                {isEditingPhilosophy && (
                    <>
                        {showError && (
                            <ErrorMessageFraud
                                closeClickHandler={() => {
                                    setShowError(false);
                                }}
                            />
                        )}
                        <div className="p-3"></div>
                        <h4 className="fw-bold">Our Philosophy</h4>
                        <div className="">
                            <textarea
                                disabled={currWorkingState === "working"}
                                ref={philosophyRef}
                                className="form-control lh-lg text-justify mb-3 py-3"
                                rows={8}
                                maxLength={1000}
                                placeholder="Tell us about your company's philosophy!"
                                defaultValue={userInfo.other?.philosophy}
                            />
                            <div className=" gap-3 mt-2 w-100 d-md-block">
                                <button className="btn btn-primary px-5" disabled={currWorkingState === "working"} type="button" onClick={savePhilosophy}>
                                    Save{" "}
                                    {currWorkingState === "working" && (
                                        <span className="ms-2">
                                            <SpinnerSmall />
                                        </span>
                                    )}
                                </button>
                                <button className="btn btn-danger ms-md-2 px-5" disabled={currWorkingState === "working"} type="button" onClick={cancelHandler}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </>
        );
    };

    const croppedImage: CSSProperties = {
        maxWidth: "100%",
        height: "300px",
        objectFit: "cover",
    };

    const [roles, setRoles] = useState<TEmployeeRole[]>([]);
    const [desiredRegion, setDesiredRegion] = useState<TGenericGUIDName[]>([]);

    useEffect(() => {
        Promise.all([getRegions(), getIndustryRoles()]).then((extraValues) => {
            setDesiredRegion(extraValues[0]);
            setRoles(extraValues[1]);
        });

        if (editable) {
            makeDroppableCapable(refProfileExternalPhoto, "external", "image", "fixed", (newfile, inappropriateContent, unsupportedImageType, errorMessage) => {
                updateExternalPhoto(newfile, inappropriateContent, unsupportedImageType, errorMessage);
                onboardingStepCompleted?.();
            });
            makeDroppableCapable(refProfileInternalPhoto, "internal", "image", "fixed", (newfile, inappropriateContent, unsupportedImageType, errorMessage) => {
                updateInternalPhoto(newfile, inappropriateContent, unsupportedImageType, errorMessage);
                onboardingStepCompleted?.();
            });
        }
        //internal photo is initially undefined unless data comes
        let _internalPhotoURL = undefined;
        if (userInfo.images?.internal) {
            _internalPhotoURL = getImagePath("internal", "fixed", !editable ? userInfo.guid : undefined) + "?" + getCacheKillQueryString();
        }
        setInternalPhoto(_internalPhotoURL);

        //external photo is initially undefined unless data comes
        let _externalPhotoURL = undefined;
        if (userInfo.images?.external) {
            _externalPhotoURL = getImagePath("external", "fixed", !editable ? userInfo.guid : undefined) + "?" + getCacheKillQueryString();
        }
        setExternalPhoto(_externalPhotoURL);
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, userInfo]);

    return (
        <div className="row justify-content-center lab-row">
            <div className="col-lg-6 bg-light rounded p-4 p-lg-5">
                <div className="row align-items-lg-center gx-lg-4">
                    <span id="upload_logo-progress"></span>
                    <div
                        className={
                            "col-12 my-2 text-center rounded " +
                            (editable ? ` ${isLogoCustomizing ? "col-lg-12 py-3 profile-logo-position-background" : "col-lg-4"}` : "col-lg-4")
                        }
                    >
                        <LogoLab
                            userInfo={userInfo}
                            editable={editable}
                            workingState={workingState}
                            isSomePartBeingEdited={isSomePartBeingEdited}
                            setInappropriateContent={setInappropriateContent}
                            setUnsupportedImageType={setUnsupportedImageType}
                            setUploadErrorMessage={setUploadErrorMessage}
                            extraClass="border-light profile-logo profile-logo_lab"
                            onCustomizeModeChange={setIsLogoCustomizing}
                            onboardingStepCompleted={onboardingStepCompleted}
                        />
                    </div>
                    <div className="col d-lg-flex flex-lg-column justify-content-lg-center">
                        <NameSection
                            editable={editable}
                            isSomePartBeingEdited={isSomePartBeingEdited}
                            setIsSomePartBeingEdited={setIsSomePartBeingEdited}
                            setUserInfo={setUserInfo}
                            updateLabAndLocalStorage={updateLabAndLocalStorage}
                            userId={userId}
                            userInfo={userInfo}
                            workingState={workingState}
                            verifyDuplicateNameAddress={verifyDuplicateNameAddress}
                        />

                        {/* Message Button*/}
                        {!editable && (
                            <>{userInfo.inmycontactlist && <MessageBtn userInfo={userInfo} className="btn-outline-secondary w-100 rounded-3 my-2" />}</>
                        )}
                    </div>
                </div>
                <div className="mt-3">
                    <span id="fill_basic_info-progress"></span>
                    <AboutTheLabSection
                        desiredRegion={desiredRegion}
                        editable={editable}
                        isSomePartBeingEdited={isSomePartBeingEdited}
                        setIsSomePartBeingEdited={setIsSomePartBeingEdited}
                        setUserInfo={setUserInfo}
                        updateLabAndLocalStorage={updateLabAndLocalStorage}
                        userInfo={userInfo}
                        workingState={workingState}
                        userLatitude={userLatitude}
                        userLongitude={userLongitude}
                        coordinatesHandler={coordinatesHandler}
                        verifyDuplicateNameAddress={verifyDuplicateNameAddress}
                    />
                </div>
                <hr className="mt-4" />
                <div className="pt-3 pb-5">
                    <PositionsSection
                        positions={roles}
                        editable={editable}
                        isSomePartBeingEdited={isSomePartBeingEdited}
                        setIsSomePartBeingEdited={setIsSomePartBeingEdited}
                        setUserInfo={setUserInfo}
                        updateLabAndLocalStorage={updateLabAndLocalStorage}
                        userId={userId}
                        userInfo={userInfo}
                        workingState={workingState}
                    />
                </div>
                {/* Invite Button*/}
                {!editable && (
                    <div className="w-100 py-4">
                        <>
                            {currentUserInfo?.type !== "admin" &&
                                !userInfo.inmycontactlist &&
                                !userInfo.invitationsenttome &&
                                userInfo.invitationsentstate !== "accepted" && (
                                    <InviteButton userToInviteId={userInfo.id} userToInviteInvitationState={userInfo.invitationsentstate} userType="lab" />
                                )}
                        </>
                    </div>
                )}
            </div>
            <div className="col-lg-6 d-flex p-0 mt-3 mt-lg-0">
                <div className="row m-0 w-100 ms-lg-3">
                    <div className="col-auto p-4 p-lg-5 bg-light rounded w-100">
                        <div className="row rounded border border-1 border-light">
                            <PhilosophySection />
                        </div>
                        <div className="row mb-5">
                            <span id="upload_office_exterior-progress"></span>
                            <span id="upload_office_interior-progress"></span>
                            <div className={"col-md-6 " + (workingState === "done" ? "" : " cssLoading")}>
                                <div className="d-flex justify-content-between py-3">
                                    <h6 className="text-muted fw-bold">Office Exterior</h6>
                                    {editable && !isSomePartBeingEdited && (
                                        <FileUploaderComponent
                                            relatedComponent={refProfileExternalPhoto}
                                            tooltipPosition="downRight"
                                            fileToolTipText="Click here to edit your business’s exterior photo, or drag and drop it onto this area.  Images should be in .JPG, .JPEG, .PNG, .HEIC or .HEIF formats. Your image should be minimum 500x500 pixels. "
                                        />
                                    )}
                                </div>
                                {editable && (
                                    <div
                                        className={
                                            !!externalPhoto
                                                ? "rounded"
                                                : "rounded dashed-border border-primary lab-external-photo p-4 d-flex justify-content-center flex-column align-items-center "
                                        }
                                        style={{
                                            ...croppedImage,
                                            [externalPhoto ? "backgroundImage" : ""]: `url(${externalPhoto})`,
                                            [externalPhoto ? "backgroundSize" : ""]: "cover",
                                        }}
                                        ref={refProfileExternalPhoto}
                                    >
                                        {!!!externalPhoto && (
                                            <>
                                                <i className="fa-solid fa-image mb-2 icon-photos"></i>
                                                <p className="d-none d-lg-block d-xl-block text-center">Drag and drop a photo of your business’s exterior</p>
                                            </>
                                        )}
                                    </div>
                                )}
                                {!editable && (
                                    <>
                                        {!!!externalPhoto && (
                                            <div className="border rounded lab-external-photo p-4 d-flex justify-content-center flex-column align-items-center">
                                                <i className="fa-solid fa-image mb-2 icon-no-photos"></i>
                                                <p
                                                    className="d-none d-lg-block d-xl-block text-center"
                                                    style={{
                                                        color: "#a5a5a5",
                                                    }}
                                                >
                                                    No photo to show
                                                </p>
                                            </div>
                                        )}
                                        {!!externalPhoto && (
                                            <div
                                                className="rounded"
                                                style={{
                                                    ...croppedImage,
                                                    backgroundImage: `url(${externalPhoto})`,
                                                    backgroundSize: "cover",
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                            <div className={"col-md-6 " + (workingState === "done" ? "" : "cssLoading")}>
                                <div className="d-flex justify-content-between py-3">
                                    <h6 className="text-muted fw-bold">Office Interior</h6>
                                    {editable && !isSomePartBeingEdited && (
                                        <FileUploaderComponent
                                            relatedComponent={refProfileInternalPhoto}
                                            tooltipPosition="downRight"
                                            fileToolTipText="Click here to edit your business’s interior photo, or drag and drop it onto this area. Images should be in .JPG, .JPEG, .PNG, .HEIC or .HEIF formats. Your image should  be minimum 500x500 pixels.  "
                                        />
                                    )}
                                </div>
                                {editable && (
                                    <div
                                        className={
                                            internalPhoto
                                                ? "rounded"
                                                : "rounded dashed-border border-primary lab-external-photo p-4 d-flex justify-content-center flex-column align-items-center"
                                        }
                                        style={{
                                            ...croppedImage,
                                            [internalPhoto ? "backgroundImage" : ""]: `url(${internalPhoto})`,
                                            [internalPhoto ? "backgroundSize" : ""]: "cover",
                                        }}
                                        ref={refProfileInternalPhoto}
                                    >
                                        {!!!internalPhoto && (
                                            <>
                                                <i className="fa-solid fa-image mb-2 icon-photos"></i>
                                                <p className="d-none d-lg-block d-xl-block text-center">Drag and drop a photo of your business’s interior</p>
                                            </>
                                        )}
                                    </div>
                                )}

                                {!editable && (
                                    <>
                                        {!!!internalPhoto && (
                                            <div className="border rounded lab-external-photo p-4 d-flex justify-content-center flex-column align-items-center">
                                                <i className="fa-solid fa-image mb-2 icon-no-photos"></i>
                                                <p
                                                    className="d-none d-lg-block d-xl-block text-center"
                                                    style={{
                                                        color: "#a5a5a5",
                                                    }}
                                                >
                                                    No photo to show
                                                </p>
                                            </div>
                                        )}
                                        {!!internalPhoto && (
                                            <div
                                                className="rounded"
                                                style={{
                                                    ...croppedImage,
                                                    backgroundImage: `url(${internalPhoto})`,
                                                    backgroundSize: "cover",
                                                }}
                                            ></div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const PartSecond = ({
    editable,
    isSomePartBeingEdited,
    userInfo,
    workingState,
    setUserInfo,
    setIsSomePartBeingEdited,
    updateLabAndLocalStorage,
    setInappropriateContent,
    setUnsupportedImageType,
    setUploadErrorMessage,
    userId: userIdByParam,
    userLatitude,
    userLongitude,
    coordinatesHandler,
    verifyDuplicateNameAddress,
    onboardingStepCompleted,
}: TAboutOurAreaProps) => {
    if (editable && !onboardingStepCompleted) {
        throw new Error("PartSecond requires 'onboardingStepCompleted' when 'editable' is true");
    }
    const [isEditingVideo, setIsEditingVideo] = useState(false);
    const refVideo = useRef<any>();
    const { userInfo: currentUserInfo } = useAuthContext();
    const Video = () => {
        const [currWorkingState, setCurrWorkingState] = useState<TProcessStates>("done");
        const editableOnClickHandler = (e: any) => {
            if (!editable) return;
            if (isSomePartBeingEdited) return;
            e.preventDefault();
            e.stopPropagation();
            setIsEditingVideo(true);
            setIsSomePartBeingEdited(true);
        };

        const saveVideo = async (e: any) => {
            e.preventDefault();
            e.stopPropagation();
            setIsEditingVideo(true);
            setCurrWorkingState("working");
            const newData = {
                ...userInfo.other,
                video: refVideo.current.value,
            };

            await updateLabAndLocalStorage(
                {
                    password: "",
                    ...newData,
                },
                "",
            );

            setUserInfo({
                ...userInfo,
                other: newData,
            });
            setIsEditingVideo(false);
            setCurrWorkingState("done");
            setIsSomePartBeingEdited(false);
        };

        const getVideoUrl = (videoLink: string) => {
            const videoId = getVideoID(videoLink);

            if (videoLink.includes("youtube.com") || videoLink.includes("youtu.be")) {
                return `https://www.youtube.com/embed/${videoId}`;
            } else if (videoLink.includes("vimeo.com")) {
                return `https://player.vimeo.com/video/${videoId}`;
            } else {
                return "";
            }
        };
        const getVideoID = (inputLink: string) => {
            if (inputLink.includes("youtube.com") || inputLink.includes("youtu.be")) {
                const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
                const id = inputLink.match(regex);
                return id ? id[1] : null;
            } else if (inputLink.includes("vimeo.com")) {
                const regex = /(?:https?:\/\/)?(?:www\.)?(?:vimeo\.com\/)([0-9]+)/;
                const vimeoId = inputLink.match(regex);
                return vimeoId ? vimeoId[1] : null;
            } else {
                return null;
            }
        };

        const cancelHandler = async () => {
            refVideo.current.value = userInfo?.other.video;
            setIsEditingVideo(false);
            setIsSomePartBeingEdited(false);
        };

        return (
            <>
                <span id="upload_video-progress"></span>
                {!isEditingVideo && (
                    <>
                        {editable && !isSomePartBeingEdited && (
                            <div
                                className="fileTooltip right ms-auto mb-3"
                                data-tooltip="Add a Video! Your video should be a YouTube or Vimeo url link. Find the video that best represents your area. On YouTube, click share below the video and then copy and paste the link into the editor. "
                                role="button"
                                onClick={editableOnClickHandler}
                            >
                                <i data-testid="edit-icon" className="fa-solid fa-pen-to-square fa-xs p-2 text-center shadow editIcon"></i>
                            </div>
                        )}
                        {currWorkingState === "done" && !userInfo.other?.video && editable && (
                            <div className={"border rounded p-4 text-center" + (workingState === "done" ? "" : "cssLoading")}>
                                <i className="fa-solid fa-video mb-2 icon-no-photos"></i>
                                <p className="m-0 text-center" style={{ color: "#a5a5a5" }}>
                                    Make your profile stand out!
                                    <br /> <strong> Click on Edit to upload a video</strong> showcasing the local environment, community, or nearby attractions.
                                    <br /> It’s a great way to make your profile more appealing.
                                </p>
                            </div>
                        )}
                        {currWorkingState === "done" && userInfo.other?.video && (
                            <div className="ratio ratio-16x9">
                                <iframe
                                    width="1100"
                                    height="725"
                                    className="rounded"
                                    src={getVideoUrl(userInfo.other?.video)}
                                    title="YouTube video player"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                ></iframe>
                            </div>
                        )}
                    </>
                )}
                {isEditingVideo && (
                    <div
                        className={
                            "w-100 mb-3 rounded dashed-border border-primary p-4 text-center " +
                            (workingState !== "done" || currWorkingState !== "done" ? "cssLoading" : "")
                        }
                    >
                        <i className="fa-brands fa-youtube icon-photos mb-2">
                            <i className="fa-brands fa-square-vimeo icon-photos ms-3"></i>
                        </i>
                        <p className="text-center">Showcase the area around your business! </p>
                        <p className="text-center mb-2">Share a YouTube or Vimeo link featuring the local environment, community, or attractions.</p>
                        <input
                            ref={refVideo}
                            className="text-center mb-2 form-control"
                            type="text"
                            defaultValue={userInfo?.other.video}
                            placeholder="Paste your video link here. (e.g. https://www.youtube.com/watch?v=tCezBBF726c)"
                        />
                        <div className="">
                            <button className="btn btn-primary px-5 me-2" type="button" onClick={saveVideo}>
                                {currWorkingState === "working" ? <SpinnerSmall /> : <>Save</>}
                            </button>
                            <button className="btn btn-danger px-5" type="button" onClick={cancelHandler}>
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </>
        );
    };

    return (
        <div className="row lab-row mt-3 mt-lg-4">
            <div className="col bg-light rounded p-4">
                <div className="row">
                    <div className="col-lg-7 p-2 p-md-4">
                        <AboutOurArea
                            setInappropriateContent={setInappropriateContent}
                            setUnsupportedImageType={setUnsupportedImageType}
                            setUploadErrorMessage={setUploadErrorMessage}
                            userId={userIdByParam}
                            editable={editable}
                            isSomePartBeingEdited={isSomePartBeingEdited}
                            setIsSomePartBeingEdited={setIsSomePartBeingEdited}
                            setUserInfo={setUserInfo}
                            updateLabAndLocalStorage={updateLabAndLocalStorage}
                            userInfo={userInfo}
                            workingState={workingState}
                            userLatitude={userLatitude}
                            userLongitude={userLongitude}
                            coordinatesHandler={coordinatesHandler}
                            verifyDuplicateNameAddress={verifyDuplicateNameAddress}
                        />
                    </div>
                    <div className="col-lg-5 p-2 p-md-4 mb-3 mb-md-0">
                        <Video />
                    </div>
                    <div className="col p-0">
                        <span id="gallery_area-progress"></span>
                        <GallerySection
                            setIsSomePartBeingEdited={setIsSomePartBeingEdited}
                            userId={Number(userIdByParam) || (currentUserInfo?.id as number)}
                            setUploadErrorMessage={setUploadErrorMessage}
                            editable={editable}
                            isSomePartBeingEdited={isSomePartBeingEdited}
                            title="GALLERY"
                            placeholder=" Click on Edit to upload photos of your business, the local environment, community, or nearby attractions. It’s a great way to make your profile more engaging."
                            onboardingStepCompleted={onboardingStepCompleted}
                            isIncognito={false}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export const AboutOurArea = ({
    editable,
    isSomePartBeingEdited,
    userInfo,
    workingState,
    setUserInfo,
    setIsSomePartBeingEdited,
    updateLabAndLocalStorage,
}: TAboutOurAreaProps) => {
    const [isEditingArea, setIsEditingArea] = useState(false);
    const refAboutArea = useRef<any>();

    const [showError, setShowError] = useState(false);
    const [currWorkingState, setCurrWorkingState] = useState<TProcessStates>("done");
    const editableOnClickHandler = (e: any) => {
        if (!editable) return;
        if (isSomePartBeingEdited) return;
        e.preventDefault();
        e.stopPropagation();
        setIsEditingArea(true);
        setIsSomePartBeingEdited(true);
    };

    const saveArea = async (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        setShowError(false);
        setCurrWorkingState("working");
        try {
            const newData = {
                ...userInfo.other,
                aboutArea: refAboutArea.current.value,
            };
            await updateLabAndLocalStorage(
                {
                    password: "",
                    ...newData,
                },
                "",
            );
            setIsEditingArea(false);

            setUserInfo({
                ...userInfo,
                other: newData,
            });
        } catch (error) {
            if (ERROR_IS_FRAUD(error)) {
                setShowError(true);
            }
            setIsEditingArea(true);
        }
        setIsSomePartBeingEdited(false);
        setCurrWorkingState("done");
    };

    const cancelHandler = async () => {
        refAboutArea.current.value = userInfo?.other.aboutArea;
        setShowError(false);
        setIsEditingArea(false);
        setIsSomePartBeingEdited(false);
    };

    return (
        <>
            <div className="d-flex justify-content-start mb-2">
                <span id="fill_about_area-progress"></span>
                <h4 className="fw-bold">ABOUT OUR AREA</h4>
                {!isEditingArea && editable && !isSomePartBeingEdited && (
                    <div className="fileTooltip leftEdit ms-auto ms-lg-3" data-tooltip=" Edit " role="button" onClick={editableOnClickHandler}>
                        <i data-testid="edit-icon" className="fa-solid fa-pen-to-square fa-xs p-2 text-center shadow editIcon"></i>
                    </div>
                )}
            </div>
            {editable && !userInfo.other.aboutArea && !isEditingArea && (
                <>
                    <div className="border rounded p-4 text-center">
                        <i className="fa-solid fa-align-left mb-2 icon-no-photos"></i>
                        <p className="m-0 text-center" style={{ color: "#a5a5a5" }}>
                            <strong> Add a personal touch to your profile! </strong>
                            <br />
                            Click on Edit to write a description about the local area, highlighting what makes your community special.
                            <br />
                            It’s a great way to make your profile more inviting.
                        </p>
                    </div>
                </>
            )}
            {!isEditingArea && (
                <>
                    {userInfo.other?.aboutArea && (
                        <div className="mb-2 mb-lg-5 text-break">
                            <p className={workingState !== "done" || currWorkingState !== "done" ? "cssLoading" : "text-start m-0"}>
                                {userInfo.other.aboutArea}
                            </p>
                        </div>
                    )}
                </>
            )}
            {isEditingArea && (
                <>
                    {showError && <ErrorMessageFraud closeClickHandler={() => setShowError(false)} />}
                    <>
                        <textarea
                            disabled={currWorkingState === "working"}
                            ref={refAboutArea}
                            className="form-control text-justify mb-2"
                            rows={7}
                            defaultValue={userInfo.other?.aboutArea}
                            maxLength={1000}
                        />
                        <div className=" gap-3 mt-2 w-100 d-md-block">
                            <button className="btn btn-primary px-5" disabled={currWorkingState === "working"} onClick={saveArea}>
                                Save{" "}
                                {currWorkingState === "working" && (
                                    <span className="ms-2">
                                        <SpinnerSmall />
                                    </span>
                                )}
                            </button>
                            <button className="btn btn-danger ms-md-2 px-5" disabled={currWorkingState === "working"} onClick={cancelHandler}>
                                Cancel
                            </button>
                        </div>
                    </>
                </>
            )}
        </>
    );
};
